'use client';

import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { websiteEnv } from '@/constants/website-env';
import { useLoadingRouter } from '@/hooks/use-loading-router';
import { cn } from '@/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { forwardRef, useMemo, useRef, useState } from 'react';
import CategoryFilterItem from './category-filter-item';
import ColorFilterItem from './color-filter-item';
import DefaultFilterItem from './default-filter-item';
import DressStyleFilterItem from './dress-style-filter-item';
import FilterDialogColorsFamilyFilter from './filter-dialog-colors-family-filter';
import { generateLinkHref } from './generate-link-href';
import { generateNextState } from './generate-next-filter-state';
import PriceSelect from './price-filter-item';
import SizeFilterItem from './size-filter-item';
import StockFilterItem from './stock-filter-item';

interface Props {
  filterOptions: {
    attribute_code: string;
    count: number;
    label: string;
    position: number;
    options: {
      count: number;
      label: string;
      swatch_data: { thumbnail: string | null; value: string | null } | null;
      value: string;
      parent_id: number;
      url_key: string;
    }[];
  }[];
  filterState: FilterState;
  curCategoryUrlKey: string;
  isSearchPage: boolean;
  hideColorsFamily: boolean;
  optionIdMapToRewrite: Record<string, Record<string, string>>;
  open: boolean;
  onSetOpen: React.Dispatch<React.SetStateAction<boolean>>;
  colorsFamilyOptions: any[];
  aggregations: any[];
  searchParams?: URLSearchParams;
}

const FilterModal = (props: Props) => {
  const {
    filterOptions,
    filterState,
    curCategoryUrlKey,
    isSearchPage,
    hideColorsFamily,
    optionIdMapToRewrite,
    open,
    onSetOpen,
    colorsFamilyOptions,
    aggregations,
    searchParams,
  } = props;
  const router = useLoadingRouter();
  const [internalFilterState, setInternalFilterState] = useState(filterState);
  const prevPropsRef = useRef({ open });

  if (prevPropsRef.current.open !== open) {
    prevPropsRef.current.open = open;
    setInternalFilterState(filterState);
  }

  const IGNORED_ATTRIBUTE_CODES = isSearchPage
    ? [COLORS_FAMILY_KEY, 'aw_general_size', 'color']
    : [COLORS_FAMILY_KEY, 'aw_general_size', 'color', 'category_id'];
  const sortedFilterOptions = useMemo(() => {
    return filterOptions.slice(0).sort((a, b) => {
      if (a.attribute_code === 'aw_product_stock_condition' && b.attribute_code !== 'aw_product_stock_condition')
        return 1;
      if (a.attribute_code !== 'aw_product_stock_condition' && b.attribute_code === 'aw_product_stock_condition')
        return -1;

      const positionA = a.position;
      const positionB = b.position;

      if (positionA === positionB) return 0;

      if (positionA === null && positionB !== null) return 1;

      if (positionA !== null && positionA === null) return -1;

      return positionA - positionB;
    });
  }, [filterOptions]);

  const [activeAttributeCode, setActiveAttributeCode] = useState(() => {
    return (
      sortedFilterOptions.find(x => !IGNORED_ATTRIBUTE_CODES.includes(x.attribute_code))?.attribute_code ||
      sortedFilterOptions[0]?.attribute_code ||
      null
    );
  });

  const handleReset = () => {
    setInternalFilterState([]);
  };

  const handleDone = () => {
    onSetOpen(false);
    const nextUrl = generateLinkHref({
      categoryUrlKey: curCategoryUrlKey,
      filterState: internalFilterState,
      isSearchPage,
      hideColorsFamily,
      colorsFamilyOptions,
      optionIdMapToRewrite,
      searchParams,
    });
    router.push(nextUrl);
  };

  const renderCurrentFilterItem = () => {
    const currentOptionInfo = sortedFilterOptions.find(x => x.attribute_code === activeAttributeCode);
    const currentOptions = currentOptionInfo?.options || [];
    const attributeLabel = currentOptionInfo?.label || '';

    if (currentOptions.length === 0 || !activeAttributeCode) {
      return null;
    }

    if (activeAttributeCode === 'aw_product_stock_condition') {
      const inStockFilter = sortedFilterOptions.find(x => x.attribute_code === 'aw_product_stock_condition')
        ?.options?.[0];
      const prevStateIndex = internalFilterState.findIndex(x => x.attributeCode === activeAttributeCode);
      const nextState = generateNextState({
        attributeCode: activeAttributeCode,
        value: inStockFilter?.value || '',
        label: inStockFilter?.label || '',
        prevStateIndex,
        prevActive: prevStateIndex !== -1,
        isSearchPage,
        optionIdMapToRewrite,
        filterState: internalFilterState,
        hideColorsFamily,
      });

      return (
        <div
          className='px-[15px]'
          key={activeAttributeCode}
        >
          <StockFilterItem
            isActive={prevStateIndex !== -1}
            onClick={() => {
              setInternalFilterState(nextState);
            }}
          />
        </div>
      );
    }

    if (activeAttributeCode === 'category_id') {
      const prevStateIndex = internalFilterState.findIndex(x => x.attributeCode === activeAttributeCode);
      const selectedCategoryOptions = prevStateIndex === -1 ? [] : internalFilterState[prevStateIndex].options || [];

      return (
        <div
          className='flex flex-col gap-y-[15px] p-[15px]'
          key={activeAttributeCode}
        >
          {currentOptions.map(x => {
            const isActive = selectedCategoryOptions.some(option => option.value === x.value);
            const nextState = generateNextState({
              attributeCode: activeAttributeCode,
              value: x.value,
              label: x.label,
              prevStateIndex,
              prevActive: isActive,
              isSearchPage,
              optionIdMapToRewrite,
              filterState: internalFilterState,
              hideColorsFamily,
            });

            return (
              <CategoryFilterItem
                label={x.label}
                key={x.url_key}
                count={x.count}
                isActive={isActive}
                onClick={() => {
                  setInternalFilterState(nextState);
                }}
              />
            );
          })}
        </div>
      );
    }

    if (activeAttributeCode === 'price') {
      const prevStateIndex = internalFilterState.findIndex(x => x.attributeCode === activeAttributeCode);
      const selectedPriceOptions = prevStateIndex === -1 ? [] : internalFilterState[prevStateIndex].options || [];

      return (
        <div
          className='px-[15px]'
          key={activeAttributeCode}
        >
          <PriceSelect
            values={currentOptions.map(x => x.value)}
            filterStateRewrite={selectedPriceOptions[0]?.rewrite || ''}
            onAfterChange={(min, max) => {
              const nextState = generateNextState({
                attributeCode: activeAttributeCode,
                value: `${min}_${max}`,
                label: `${min}-${max}`,
                prevStateIndex,
                prevActive: false,
                isSearchPage,
                optionIdMapToRewrite,
                filterState: internalFilterState,
                hideColorsFamily,
              });

              setInternalFilterState(nextState);
            }}
          />
        </div>
      );
    }

    if (activeAttributeCode === 'custom_option_color_ids') {
      const prevStateIndex = internalFilterState.findIndex(x => x.attributeCode === activeAttributeCode);
      const selectedColorOptions = prevStateIndex === -1 ? [] : internalFilterState[prevStateIndex].options || [];

      if (hideColorsFamily || isSearchPage) {
        return (
          <div
            className={clsx('flex flex-col px-2.5')}
            key={activeAttributeCode}
          >
            {currentOptions.map(x => {
              const isActive = selectedColorOptions.some(option => option.value === x.value);
              const nextState = generateNextState({
                attributeCode: activeAttributeCode,
                value: x.value,
                label: x.label,
                prevStateIndex,
                prevActive: isActive,
                isSearchPage,
                optionIdMapToRewrite,
                filterState: internalFilterState,
                hideColorsFamily,
              });

              return (
                <ColorFilterItem
                  label={x.label}
                  swatch_data={x.swatch_data}
                  isActive={isActive}
                  onClick={() => {
                    setInternalFilterState(nextState);
                  }}
                  key={x.value}
                />
              );
            })}
          </div>
        );
      }

      return (
        <FilterDialogColorsFamilyFilter
          colorsFamilyOptions={colorsFamilyOptions}
          aggregations={aggregations}
          filterState={internalFilterState}
          onSetFilterState={setInternalFilterState}
          key={activeAttributeCode}
        />
      );
    }

    if (activeAttributeCode === 'custom_option_size_ids') {
      const prevStateIndex = internalFilterState.findIndex(x => x.attributeCode === activeAttributeCode);
      const selectedSizeOptions = prevStateIndex === -1 ? [] : internalFilterState[prevStateIndex].options || [];

      return (
        <div
          className=' flex flex-row flex-wrap gap-x-2 gap-y-2.5 px-[15px] py-2.5'
          key={activeAttributeCode}
        >
          {currentOptions.map(x => {
            const isActive = selectedSizeOptions.some(option => option.value === x.value);
            const nextState = generateNextState({
              attributeCode: activeAttributeCode,
              value: x.value,
              label: x.label,
              prevStateIndex,
              prevActive: isActive,
              isSearchPage,
              optionIdMapToRewrite,
              filterState: internalFilterState,
              hideColorsFamily,
            });

            return (
              <SizeFilterItem
                label={x.label}
                isActive={isActive}
                onClick={() => {
                  setInternalFilterState(nextState);
                }}
                key={x.value}
              />
            );
          })}
        </div>
      );
    }

    if (
      activeAttributeCode === 'aw_dresses_silhouette' ||
      activeAttributeCode === 'aw_dresses_hemline' ||
      activeAttributeCode === 'aw_dresses_neckline' ||
      activeAttributeCode === 'aw_dresses_backdetail' ||
      activeAttributeCode === 'aw_dresses_straps_sleeves'
    ) {
      const prevStateIndex = internalFilterState.findIndex(x => x.attributeCode === activeAttributeCode);
      const selectedDressStyleOptions = prevStateIndex === -1 ? [] : internalFilterState[prevStateIndex].options || [];

      return (
        <div
          className={clsx('grid grid-cols-3 gap-x-[7px] gap-y-5 px-[15px]  pt-5  ')}
          key={activeAttributeCode}
        >
          {currentOptions.map((x, i) => {
            const isActive = selectedDressStyleOptions.some(option => option.value === x.value);
            const nextState = generateNextState({
              attributeCode: activeAttributeCode,
              value: x.value,
              label: x.label,
              prevStateIndex,
              prevActive: isActive,
              isSearchPage,
              optionIdMapToRewrite,
              filterState: internalFilterState,
              hideColorsFamily,
            });

            return (
              <DressStyleFilterItem
                label={x.label}
                isActive={isActive}
                onClick={() => {
                  setInternalFilterState(nextState);
                }}
                attributeLabel={attributeLabel}
                key={x.value}
              />
            );
          })}
        </div>
      );
    }

    const prevStateIndex = internalFilterState.findIndex(x => x.attributeCode === activeAttributeCode);
    const selectedOptions = prevStateIndex === -1 ? [] : internalFilterState[prevStateIndex].options || [];

    return (
      <div
        className={clsx(' flex flex-col gap-y-[30px] p-[15px]')}
        key={activeAttributeCode}
      >
        {currentOptions.map((x, i) => {
          const isActive = selectedOptions.some(option => option.value === x.value);
          const nextState = generateNextState({
            attributeCode: activeAttributeCode,
            value: x.value,
            label: x.label,
            prevStateIndex,
            prevActive: isActive,
            isSearchPage,
            optionIdMapToRewrite,
            filterState: internalFilterState,
            hideColorsFamily,
          });

          return (
            <DefaultFilterItem
              label={x.label}
              isActive={isActive}
              onClick={() => {
                setInternalFilterState(nextState);
              }}
              count={x.count}
              key={x.value}
            />
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onSetOpen}
    >
      <DialogContent aria-describedby={undefined}>
        <div className='flex h-full flex-col'>
          <div className='flex h-[60px] flex-none items-center justify-between border-b-1 border-solid border-E7DED5 px-2.5 '>
            <span className='font-Shango text-[22px]'>FILTERS</span>
            <button
              type='button'
              className='flex h-[46px] w-[46px] flex-none items-center justify-center'
              onClick={() => {
                onSetOpen(false);
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                fill='none'
                viewBox='0 0 18 18'
              >
                <path
                  stroke='#80583F'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='1.5'
                  d='M17 17 1 1m16 0L1 17'
                ></path>
              </svg>
            </button>
          </div>
          <div className='flex flex-1 items-start overflow-hidden'>
            <ul className='flex h-full flex-none flex-col overflow-auto'>
              {sortedFilterOptions.map(x => {
                if (!x) return null;

                const { attribute_code } = x;

                if (IGNORED_ATTRIBUTE_CODES.includes(attribute_code)) {
                  return null;
                }

                const hasSelectedItem = internalFilterState.some(x => x.attributeCode === attribute_code);
                const isStockFilter = attribute_code === 'aw_product_stock_condition';
                let renderLabel = x.label;

                if (isStockFilter) {
                  renderLabel = 'Ship in 48hrs';

                  if (!websiteEnv.isUnitedStates) {
                    return null;
                  }
                }

                if (websiteEnv.isUnitedKingdom && renderLabel === 'Color') {
                  renderLabel = 'Colour';
                }

                return (
                  <li
                    key={x.attribute_code}
                    className={clsx(isStockFilter && 'order-[100]')}
                  >
                    <button
                      type='button'
                      className={clsx(
                        'flex min-h-[50px] w-full items-center justify-start pl-3 pr-5',
                        activeAttributeCode === attribute_code
                          ? 'border-l-BE9983 bg-f8f3ef'
                          : 'border-l-transparent bg-white',
                        'border-l-3 border-solid',
                        'border-b-1 border-b-E7DED5 text-[15px]',
                        'relative',
                        hasSelectedItem &&
                          clsx(
                            'after_absolute after_right-2 after_top-1/2 after_-translate-y-1/2 after_content-[""]',
                            'after_h-[5px] after_w-[5px] after_rounded-[50%] after_bg-80583F',
                          ),
                      )}
                      onClick={() => {
                        setActiveAttributeCode(attribute_code);
                      }}
                    >
                      {renderLabel}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className='h-full flex-1 overflow-auto bg-f8f3ef'>{renderCurrentFilterItem()}</div>
          </div>
          <div className='flex flex-none  items-center justify-end gap-x-2.5 px-[15px] py-2.5 shadow-[4px_0_8px_0_rgba(0,0,0,0.1)]'>
            <button
              type='button'
              className='flex h-[45px] w-[110px] items-center  justify-center rounded-[3px] border-1 border-solid border-80583F bg-white text-sm font-medium leading-none text-80583F'
              onClick={handleReset}
            >
              RESET
            </button>
            <button
              type='button'
              className='flex h-[45px] w-[110px] items-center justify-center rounded-[3px] bg-80583F text-sm font-medium leading-none text-white'
              onClick={handleDone}
            >
              DONE({internalFilterState.length})
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilterModal;

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[200]  data-[state=open]_animate-in data-[state=closed]_animate-out data-[state=closed]_fade-out-0 data-[state=open]_fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'duration-200 fixed left-[50%] top-[50%] z-[210] h-screen w-full  -translate-x-1/2 -translate-y-1/2 bg-white data-[state=open]_animate-in data-[state=closed]_animate-out data-[state=closed]_fade-out-0 data-[state=open]_fade-in-0  data-[state=closed]_zoom-out-95 data-[state=open]_zoom-in-95 data-[state=closed]_slide-out-to-left-1/2 data-[state=closed]_slide-out-to-top-[48%]  data-[state=open]_slide-in-from-left-1/2 data-[state=open]_slide-in-from-top-[48%] supports-[height:100dvh]_h-[100dvh]',
        className,
      )}
      {...props}
    >
      <DialogTitle>Filter Modal</DialogTitle>
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className='hidden'
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
